import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {interval, Observable, of, tap} from "rxjs";
import {GenericJob, Job} from '../../_models/jobs/job';
import {BaseComponent} from "../../shared/base.component";
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {ApiService} from "../../services/api/api.service";
import {SeriesCorrectionJob} from "../../_models/jobs/series-correction-job";
import {User} from "../../_models/users";
import {PaginationDataSource} from "../../services/api/pagination-data-source";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort, MatSortModule} from "@angular/material/sort";
import {ListResponse} from "../../services/api/response-types";
import {JOB_STATUS} from "../constants";
import {JobDataService} from "../data/job-data.service";
import {DateTimePeriodService} from "../../services/date-time-period.service";
import {map, take, takeUntil} from "rxjs/operators";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {JobStatusComponent} from "../job-status/job-status.component";
import {CommonModule} from "@angular/common";
import {COMMON_TABLE_IMPORTS} from "../../shared/import-groups/Common";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {MatTooltip} from "@angular/material/tooltip";
import {RouterLink} from "@angular/router";

@Component({
    selector: 'src-jobs-table',
    templateUrl: './jobs-table.component.html',
    styleUrls: ['./jobs-table.component.scss', '../../shared/styles/shared-table-styles.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        JobStatusComponent,
        COMMON_TABLE_IMPORTS,
        SharedPipesModule,
        MatTooltip,
        MatSortModule,
        RouterLink
    ],
})
export class JobsTableComponent extends BaseComponent implements AfterViewInit {
    private _scopeType: string;
    private _status: string;
    private _createdOn: Date;
    private _changedOn: Date;

    job_status = JOB_STATUS;

    @Input() set scopeType(scopeType: string) {
        if (scopeType || this._scopeType) {
            this._scopeType = scopeType;
            this.emitFilterQuery();
        }
        this._scopeType = scopeType;
    };

    get scopeType(): string {
        return this._scopeType;
    }

    @Input() set status(status: string) {
        if (status || this._status) {
            this._status = status;
            this.emitFilterQuery();
        }
        this._status = status;
    };

    get status(): string {
        return this._status;
    }

    @Input() set createdOn(createdOn: Date) {
        if (createdOn || this._createdOn) {
            this._createdOn = createdOn;
            this.emitFilterQuery();
        }
        this._createdOn = createdOn;
    };

    get createdOn(): Date {
        return this._createdOn;
    }

    @Input() set changedOn(changedOn: Date) {
        if (changedOn || this._changedOn) {
            this._changedOn = changedOn;
            this.emitFilterQuery();
        }
        this._changedOn = changedOn;
    };

    get changedOn(): Date {
        return this._changedOn;
    }

    @Input() available_types: string[];

    displayedColumns: string[] = ['details', 'type', 'created_on', 'changed_on', 'total_time', 'created_by_name', 'status'];
    pageSize: number = 10;
    filterString: string = '';

    dataSource: PaginationDataSource<GenericJob>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    private pageRange: string = "this full week";

    constructor(private api: ApiService,
                private dateTimePeriodService: DateTimePeriodService,
                private dateInst: DateTimeInstanceService,
                private jobData: JobDataService,
                private changeDetectorRef: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit() {
        this.dateInst.dateTimePeriodInitialised$.pipe(take(1), tap((dtp) => {
            if (this.dateTimePeriodService.read_dtp_from_url === true) {
                this.dateTimePeriodService.read_dtp_from_url = null;
            } else {
                this.dateInst.dtp = this.dateTimePeriodService.getDTP(this.pageRange, false);
                this.dateInst.emitDateTimePeriodChanged(this.dateInst.dtp);
            }
        })).subscribe(() => this.setDataSource());

        // Subscribe to DTP refresh for future updates (optional)
        this.dateInst.dateTimePeriodRefreshed$.pipe(takeUntil(this.onDestroy)).subscribe(dtp => {
            this.emitFilterQuery();
        });

        // Auto-refresh every 30 seconds to update elapsed time for running jobs
        interval(30000).pipe(
            takeUntil(this.onDestroy),
            tap(() => {
                if (this.dataSource) {
                    // Trigger change detection to update displayed times
                    this.dataSource.refresh();
                    this.changeDetectorRef.markForCheck();
                }
            })
        ).subscribe();

    }

    setDataSource(): void {
        const initialQuery = this.getInitialSearchOptions();
        this.paginator.pageSize = this.pageSize;

        // @ts-ignore
        this.dataSource = new PaginationDataSource<Job>(
            (query) => this.pageJobs(query),
            initialQuery,
            this.paginator,
            this.sort
        );
    }

    getInitialSearchOptions(): SearchQueryOptions {
        const options = new SearchQueryOptions();
        options.filters = this.getJobsFilter();
        options.page_number = 1;
        return options;
    }

    getJobsFilter() {
        return this.jobData.getJobsFilter(this.createdOn, this.changedOn, this.status, this.scopeType);
    }

    // @ts-ignore
    pageJobs(options: SearchQueryOptions): Observable<ListResponse<Job>> {
        return this.api.job.searchMany(options);
    }

    emitFilterQuery() {
        if (!this.dataSource) {
            return;
        }
        const filters = this.getJobsFilter();
        this.dataSource.filterBy(filters);
    }

    getExecutionTime(job: SeriesCorrectionJob): string {
        if (!job.attributes.created_on) {
            return "-";
        }

        const start = new Date(job.attributes.created_on);
        const isCompleted = ["completed", "error"].includes(job.attributes.status);

        if (isCompleted && job.attributes.changed_on) {
            // Completed: show total duration
            const end = new Date(job.attributes.changed_on);
            const deltaMs = end.getTime() - start.getTime();
            return this.formatDuration(deltaMs);
        } else {
            // Pending/Processing: show elapsed time
            const now = new Date();
            const deltaMs = now.getTime() - start.getTime();
            return this.formatDuration(deltaMs);
        }
    }

    formatDuration(milliseconds: number): string {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days}d ${hours % 24}h`;
        }
        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        }
        if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        }
        if (seconds > 0) {
            return `${seconds}s`;
        }
        return "< 1s";
    }

}
